/**
 * Single source of truth for the SEO <title> / meta-description strings used by
 * the three feature catalog pages:
 *   - client/src/pages/features-index.tsx       → indexSEO()
 *   - client/src/pages/features-group.tsx       → groupSEO(group)
 *   - client/src/pages/features-subfeature.tsx  → subFeatureSEO(group, sf)
 *
 * Both the page components and the SEO-uniqueness test
 * (client/src/__tests__/feature-catalog-seo-titles.test.ts) import these
 * builders, so the test can never drift from what the pages actually render.
 */
import {
  type FeatureGroup,
  type SubFeature,
} from "@/data/feature-catalog";
import { marketingFeatureCatalog, marketingFeatureCount } from "@/data/marketing-catalog";

/** Brand suffix appended to every feature-page <title>. */
export const TITLE_SUFFIX = " | PadPulse";

/** Total sub-features the public site lists (cut/hidden features excluded). */
export const totalFeatureCount = marketingFeatureCount;

export interface FeatureSEO {
  /** Short page name for the browser tab ("<tabTitle> · PadPulse"). */
  tabTitle: string;
  /** Full document <title> / og:title. */
  title: string;
  /** Meta description / og:description. */
  description: string;
}

/** SEO strings for the /features index page. */
export function indexSEO(): FeatureSEO {
  return {
    tabTitle: "Features",
    title: `All Features — Co-Living Management${TITLE_SUFFIX}`,
    description: `Explore all ${totalFeatureCount}+ features across ${marketingFeatureCatalog.length} groups — messaging, property management, research and underwriting, member management, operations, vendors, revenue, and integrations — built specifically for PadSplit hosts.`,
  };
}

/** SEO strings for a /features/:group overview page. */
export function groupSEO(group: FeatureGroup): FeatureSEO {
  return {
    tabTitle: `${group.name} Features`,
    title: `${group.name} — Features${TITLE_SUFFIX}`,
    description: group.description,
  };
}

/** SEO strings for a /features/:group/:feature sub-feature page. */
export function subFeatureSEO(group: FeatureGroup, sf: SubFeature): FeatureSEO {
  return {
    tabTitle: `${sf.name} — ${group.name}`,
    title: `${sf.name} — ${group.name}${TITLE_SUFFIX}`,
    description: sf.description,
  };
}
